/**
 * All contact / capture forms.
 * Each posts to a corresponding /api/ghl/* endpoint on the backend,
 * which forwards to a configured GoHighLevel inbound webhook.
 */
import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck, HandHelping, Phone } from "lucide-react";
import { submitForm } from "@/lib/api";
import { ORG, ORG_ADDRESS_LINE } from "@/lib/org";

/* --- shared field wrappers --- */
function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid ? `${testid}-field` : undefined}>
      <span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-white/55 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function DarkInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-white/[0.04] border border-[#27272A] px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[var(--brand-red)] focus:bg-white/[0.06] transition-colors rounded-none"
    />
  );
}

function DarkTextarea({ testid, ...props }) {
  return (
    <textarea
      data-testid={testid}
      {...props}
      className="w-full bg-white/[0.04] border border-[#27272A] px-4 py-3 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-[var(--brand-red)] focus:bg-white/[0.06] transition-colors rounded-none min-h-[130px]"
    />
  );
}

function LightInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-[#FAFAF8] border border-zinc-200 px-4 py-3 text-black text-sm placeholder:text-zinc-400 focus:outline-none focus:border-[var(--brand-red)] focus:bg-white transition-colors rounded-none"
    />
  );
}

function LightSelect({ testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className="w-full bg-[#FAFAF8] border border-zinc-200 px-4 py-3 text-black text-sm focus:outline-none focus:border-[var(--brand-red)] focus:bg-white transition-colors rounded-none"
    >
      {children}
    </select>
  );
}

function DarkSelect({ testid, children, ...props }) {
  return (
    <select
      data-testid={testid}
      {...props}
      className="w-full bg-white/[0.04] border border-[#27272A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[var(--brand-red)] focus:bg-white/[0.06] transition-colors rounded-none"
    >
      {children}
    </select>
  );
}

function SubmitButton({ loading, children, testid }) {
  return (
    <button
      type="submit"
      disabled={loading}
      data-testid={testid}
      className="btn-primary-sharp w-full justify-center"
    >
      {loading ? (
        <>
          <Loader2 size={14} className="animate-spin" /> Submitting…
        </>
      ) : (
        <>
          {children} <ArrowRight size={14} />
        </>
      )}
    </button>
  );
}

/* --- helper --- */
async function handle(formKey, payload, resetForm, successMsg) {
  try {
    const res = await submitForm(formKey, payload);
    if (res?.delivered_to_ghl) {
      toast.success(successMsg);
    } else if (res?.webhook_configured) {
      toast.success(`${successMsg} (Delivery pending — our team will follow up.)`);
    } else {
      toast.success(`${successMsg} We've received your submission.`);
    }
    resetForm?.();
    return true;
  } catch (err) {
    toast.error("Something went wrong. Please try again or call (773) 940-5265.");
    return false;
  }
}

/* ============ New Here / Plan Your Visit ============ */
export function NewHere() {
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    const f = e.currentTarget;
    setLoading(true);
    const data = {
      first_name: f.first_name.value.trim(),
      last_name: f.last_name.value.trim(),
      email: f.email.value.trim(),
      phone: f.phone.value.trim(),
      service: f.service.value,
      how_heard: f.how_heard.value.trim(),
      message: "New Here — Plan a Visit form submission",
    };
    await handle("visit", data, () => f.reset(), "Thanks — we can’t wait to meet you!");
    setLoading(false);
  }

  return (
    <section
      id="new-here"
      data-testid="new-here-section"
      className="section bg-[var(--bg-tertiary)]"
    >
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <div>
          <p className="eyebrow mb-3">First Time?</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Plan Your <br /> First <span className="brand-red">Visit</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            We’d love to meet you. Fill this out and our welcome team will
            personally reach out with everything you need to know before you
            arrive — parking, kids, what to expect, all of it.
          </p>

          <div className="mt-10 grid gap-4">
            {[
              { t: "Come As You Are", d: "No dress code, no pressure — just come." },
              { t: "Kids Are Welcome", d: "RPK provides safe, fun ministry for kids." },
              { t: "Meet The Team", d: "Stop by Connect Point after service." },
              { t: "First-Time Gift", d: "A welcome gift is waiting for you." },
            ].map((x) => (
              <div
                key={x.t}
                data-testid={`new-here-perk-${x.t.toLowerCase().replace(/\s+/g, "-")}`}
                className="flex gap-4 border border-[#27272A] p-4 hover:border-[var(--brand-red)]/40 transition-colors"
              >
                <span className="w-11 h-11 min-w-11 grid place-items-center bg-[var(--brand-red)]/15 brand-red">
                  <ShieldCheck size={16} />
                </span>
                <div>
                  <h4 className="font-display font-bold uppercase text-white text-sm tracking-wide">
                    {x.t}
                  </h4>
                  <p className="mt-1 text-xs text-white/50 leading-relaxed">
                    {x.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white/[0.03] border border-[#27272A] p-8 md:p-10"
          data-testid="new-here-form"
        >
          <p className="font-display font-black uppercase text-2xl text-white mb-6">
            Plan Your Visit
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="First Name" testid="first-name">
              <DarkInput name="first_name" required placeholder="John" testid="new-here-first-name" />
            </Field>
            <Field label="Last Name" testid="last-name">
              <DarkInput name="last_name" required placeholder="Smith" testid="new-here-last-name" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="Email">
              <DarkInput type="email" name="email" required placeholder="john@example.com" testid="new-here-email" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="Phone">
              <DarkInput type="tel" name="phone" placeholder="(312) 555-0000" testid="new-here-phone" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="I plan to attend">
              <DarkSelect name="service" defaultValue="" testid="new-here-service">
                <option value="" disabled>Select a service</option>
                <option>Sunday Worship Experience (12:30 PM)</option>
                <option>The Classroom (Mon 7:30 PM)</option>
                <option>Fire Nights (2nd Thursday)</option>
                <option>Prophetic Night South (4th Sunday)</option>
              </DarkSelect>
            </Field>
          </div>
          <div className="mb-6">
            <Field label="How did you hear about us?">
              <DarkInput name="how_heard" placeholder="Social media, a friend…" testid="new-here-how-heard" />
            </Field>
          </div>
          <SubmitButton loading={loading} testid="new-here-submit">
            Submit &amp; Plan My Visit
          </SubmitButton>
        </form>
      </div>
    </section>
  );
}

/* ============ Prayer Request ============ */
export function PrayerRequest() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e) {
    e.preventDefault();
    const f = e.currentTarget;
    setLoading(true);
    const data = {
      first_name: f.first_name.value.trim(),
      last_name: f.last_name.value.trim(),
      email: f.email.value.trim(),
      request_type: f.request_type.value,
      message: f.message.value.trim(),
    };
    await handle("prayer", data, () => f.reset(), "Thank you — we are believing with you.");
    setLoading(false);
  }
  return (
    <section
      id="prayer"
      data-testid="prayer-section"
      className="section bg-[var(--bg-secondary)]"
    >
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <div>
          <p className="eyebrow mb-3">We’re Believing With You</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Submit A <br /> <span className="brand-red">Prayer</span> Request
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            You don’t have to carry it alone. Our pastoral team reads every
            prayer request and stands with you in faith.
          </p>
          <div className="mt-8 space-y-3 text-sm">
            <div className="flex items-center gap-3 text-white/55">
              <ShieldCheck size={16} className="text-[var(--brand-yellow)]" />
              All requests are kept confidential
            </div>
            <div className="flex items-center gap-3 text-white/55">
              <HandHelping size={16} className="text-[var(--brand-yellow)]" />
              Our team prays over every request
            </div>
            <div className="flex items-center gap-3 text-white/55">
              <Phone size={16} className="text-[var(--brand-yellow)]" />
              <a href="tel:7739405265" className="hover:text-white">
                (773) 940-5265
              </a>{" "}
              for urgent needs
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white/[0.03] border border-[#27272A] p-8 md:p-10"
          data-testid="prayer-form"
        >
          <p className="font-display font-black uppercase text-2xl text-white mb-6">
            Prayer Request
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="First Name">
              <DarkInput name="first_name" required placeholder="Your name" testid="prayer-first-name" />
            </Field>
            <Field label="Last Name">
              <DarkInput name="last_name" placeholder="Last name" testid="prayer-last-name" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="Email">
              <DarkInput type="email" name="email" placeholder="your@email.com" testid="prayer-email" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="Type of Request">
              <DarkSelect name="request_type" defaultValue="" testid="prayer-type">
                <option value="" disabled>Select</option>
                <option>Healing &amp; Health</option>
                <option>Family &amp; Relationships</option>
                <option>Financial Breakthrough</option>
                <option>Spiritual Growth</option>
                <option>Purpose &amp; Direction</option>
                <option>Salvation of a Loved One</option>
                <option>Other</option>
              </DarkSelect>
            </Field>
          </div>
          <div className="mb-6">
            <Field label="Your Prayer Request">
              <DarkTextarea
                name="message"
                required
                placeholder="Share your prayer request. We are believing with you…"
                testid="prayer-message"
              />
            </Field>
          </div>
          <SubmitButton loading={loading} testid="prayer-submit">
            Submit Prayer Request
          </SubmitButton>
        </form>
      </div>
    </section>
  );
}

/* ============ Volunteer Signup ============ */
export function Volunteer() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e) {
    e.preventDefault();
    const f = e.currentTarget;
    setLoading(true);
    const data = {
      first_name: f.first_name.value.trim(),
      last_name: f.last_name.value.trim(),
      email: f.email.value.trim(),
      phone: f.phone.value.trim(),
      interest: f.interest.value,
      message: f.message.value.trim(),
    };
    await handle("volunteer", data, () => f.reset(), "Thanks for stepping up — we'll be in touch!");
    setLoading(false);
  }
  return (
    <section
      id="volunteer"
      data-testid="volunteer-section"
      className="section bg-[var(--bg-primary)]"
    >
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <div>
          <p className="eyebrow mb-3">Serve With Us</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-white">
            Volunteer <span className="brand-red">//</span> Sign Up
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed">
            Every hand matters. Whether it’s hospitality, tech, prayer, worship,
            outreach, or kids — there’s a place for your gift here.
          </p>
          <ul className="mt-8 grid gap-2 text-sm text-white/55">
            {["Hospitality & Welcome", "Media, Tech & Production", "Worship & Creative", "RPK Kids Ministry", "GO Teams (Outreach)", "Prayer & Intercession"].map(
              (x) => (
                <li key={x} className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 bg-[var(--brand-red)]" /> {x}
                </li>
              ),
            )}
          </ul>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white/[0.03] border border-[#27272A] p-8 md:p-10 w-full"
          data-testid="volunteer-form"
        >
          <p className="font-display font-black uppercase text-2xl text-white mb-6">
            Volunteer Signup
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="First Name">
              <DarkInput name="first_name" required testid="volunteer-first-name" />
            </Field>
            <Field label="Last Name">
              <DarkInput name="last_name" required testid="volunteer-last-name" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="Email">
              <DarkInput type="email" name="email" required testid="volunteer-email" />
            </Field>
            <Field label="Phone">
              <DarkInput type="tel" name="phone" testid="volunteer-phone" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="I'm Interested In">
              <DarkSelect name="interest" defaultValue="" testid="volunteer-interest">
                <option value="" disabled>Select an area</option>
                <option>Hospitality &amp; Welcome</option>
                <option>Media, Tech &amp; Production</option>
                <option>Worship &amp; Creative</option>
                <option>RPK Kids Ministry</option>
                <option>GO Teams (Outreach)</option>
                <option>Prayer &amp; Intercession</option>
                <option>Wherever needed</option>
              </DarkSelect>
            </Field>
          </div>
          <div className="mb-6">
            <Field label="Tell us about you">
              <DarkTextarea name="message" placeholder="Skills, availability, questions…" testid="volunteer-message" />
            </Field>
          </div>
          <SubmitButton loading={loading} testid="volunteer-submit">
            Send Application
          </SubmitButton>
        </form>
      </div>
    </section>
  );
}

/* ============ Contact ============ */
export function Contact() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e) {
    e.preventDefault();
    const f = e.currentTarget;
    setLoading(true);
    const data = {
      first_name: f.first_name.value.trim(),
      last_name: f.last_name.value.trim(),
      email: f.email.value.trim(),
      phone: f.phone.value.trim(),
      subject: f.subject.value.trim(),
      message: f.message.value.trim(),
    };
    await handle("contact", data, () => f.reset(), "Message received — we'll respond soon.");
    setLoading(false);
  }
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section bg-[var(--brand-cream)] text-black"
    >
      <div className="container-x grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
        <div>
          <p className="eyebrow mb-3">Get In Touch</p>
          <h2 className="font-display font-black uppercase text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-black">
            Connect <br /> <span className="brand-red">With Us</span>
          </h2>
          <p className="mt-6 text-zinc-700 leading-relaxed">
            We would love to connect with you, answer your questions, pray with
            you, and help you find your place at RE//FORM PLACE CHI.
          </p>

          <div className="mt-10 grid gap-6">
            {[
              { icon: Phone, t: "Phone", d: ORG.phone, href: ORG.phoneHref },
              { icon: HandHelping, t: "Email", d: ORG.email, href: `mailto:${ORG.email}` },
              { icon: ShieldCheck, t: "Location", d: ORG_ADDRESS_LINE },
              { icon: ShieldCheck, t: "Sunday Service", d: "12:30 PM CST" },
            ].map((x) => {
              const Icon = x.icon;
              const inner = (
                <>
                  <span className="w-11 h-11 min-w-11 grid place-items-center bg-black brand-red">
                    <Icon size={16} />
                  </span>
                  <div>
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mb-1">
                      {x.t}
                    </h4>
                    <p className="text-black font-medium">{x.d}</p>
                  </div>
                </>
              );
              return x.href ? (
                <a
                  key={x.t}
                  href={x.href}
                  className="flex items-center gap-4 hover:opacity-80 transition-opacity"
                  data-testid={`contact-info-${x.t.toLowerCase()}`}
                >
                  {inner}
                </a>
              ) : (
                <div
                  key={x.t}
                  className="flex items-center gap-4"
                  data-testid={`contact-info-${x.t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {inner}
                </div>
              );
            })}
          </div>

          <div className="mt-10 w-full border border-black/10 bg-white p-8" data-testid="contact-org-card">
            <p className="font-display font-bold uppercase text-black text-lg">
              RE<span className="brand-red">//</span>FORM PLACE CHI
            </p>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mt-1">{ORG.status}</p>
            <dl className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">Address</dt>
                <dd className="text-black mt-1">{ORG_ADDRESS_LINE}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">Tax ID / EIN</dt>
                <dd className="text-black mt-1">{ORG.ein}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">Website</dt>
                <dd className="text-black mt-1">{ORG.domain}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">Giving</dt>
                <dd className="text-black mt-1">{ORG.giveEmail}</dd>
              </div>
            </dl>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white p-8 md:p-12 border border-black/5"
          data-testid="contact-form"
        >
          <p className="font-display font-black uppercase text-2xl text-black mb-6">
            Send Us A Message
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="First Name">
              <LightInput name="first_name" required testid="contact-first-name" />
            </Field>
            <Field label="Last Name">
              <LightInput name="last_name" required testid="contact-last-name" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Field label="Email">
              <LightInput type="email" name="email" required testid="contact-email" />
            </Field>
            <Field label="Phone">
              <LightInput type="tel" name="phone" testid="contact-phone" />
            </Field>
          </div>
          <div className="mb-4">
            <Field label="Subject">
              <LightSelect name="subject" defaultValue="" testid="contact-subject">
                <option value="" disabled>Select a topic</option>
                <option>New Here — General Question</option>
                <option>Membership &amp; Next Steps</option>
                <option>Pastoral Care</option>
                <option>Volunteer / Serve</option>
                <option>Media / Press</option>
                <option>Partnership / Sponsorship</option>
                <option>Other</option>
              </LightSelect>
            </Field>
          </div>
          <div className="mb-6">
            <Field label="Message">
              <textarea
                name="message"
                required
                placeholder="How can we help?"
                data-testid="contact-message"
                className="w-full bg-[#FAFAF8] border border-zinc-200 px-4 py-3 text-black text-sm placeholder:text-zinc-400 focus:outline-none focus:border-[var(--brand-red)] focus:bg-white transition-colors rounded-none min-h-[130px]"
              />
            </Field>
          </div>
          <SubmitButton loading={loading} testid="contact-submit">
            Send Message
          </SubmitButton>
        </form>
      </div>
    </section>
  );
}

/* ============ Newsletter ============ */
export function Newsletter() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e) {
    e.preventDefault();
    const f = e.currentTarget;
    setLoading(true);
    await handle("newsletter", { email: f.email.value.trim() }, () => f.reset(), "You're subscribed. Welcome to the family!");
    setLoading(false);
  }
  return (
    <section
      id="newsletter"
      data-testid="newsletter-section"
      className="bg-[var(--brand-red)] py-16"
    >
      <div className="container-x flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <div>
          <h2 className="font-display font-black uppercase text-3xl md:text-5xl leading-[0.95] tracking-tight text-white">
            Stay Connected <br /> With RE<span className="text-black/50">//</span>FORM
          </h2>
          <p className="mt-3 text-white/85 max-w-md">
            Weekly updates, event announcements, sermon releases, and
            encouragement — direct to your inbox.
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          className="flex w-full md:w-auto min-w-[320px]"
          data-testid="newsletter-form"
        >
          <input
            type="email"
            name="email"
            required
            placeholder="Enter your email"
            data-testid="newsletter-email"
            className="flex-1 bg-white/15 px-5 py-4 text-white placeholder:text-white/60 border-0 focus:outline-none focus:bg-white/25 rounded-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="newsletter-submit"
            className="px-6 py-4 bg-black text-white text-[11px] font-bold uppercase tracking-[0.22em] hover:bg-[#1a1a1a] transition-colors flex items-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "Subscribe"}
          </button>
        </form>
      </div>
    </section>
  );
}
